/**
 * Manager for creating and configuring Runescape rune meshes
 * Provides single rune creation and composition helpers for arranging multiple runes
 */

import {
  Color3,
  Mesh,
  PBRMaterial,
  type Scene,
  Vector3,
  VertexData,
} from "@babylonjs/core";
import { BaseManager } from "../core/BaseManager.js";
import {
  getRuneMaterials,
  getRuneMeshes,
  RUNE_VARIANTS,
  type RuneMaterials,
  type RuneMesh,
  type RuneVariant,
} from "./runeData/index.js";

// Re-export types for convenience
export { RUNE_VARIANTS, type RuneVariant } from "./runeData/index.js";

/**
 * Default configuration for rune stage
 * Single source of truth - import in StageControlsService and rune.stage.ts
 */
export const DEFAULT_RUNE_STAGE_CONFIG = {
  count: 11,
  scale: 1,
  variants: [
    // Elemental runes (RuneScape Classic, 2001)
    "air",
    "water",
    "earth",
    "fire",
    // Basic catalytic runes (RuneScape Classic, 2001)
    "mind",
    "body",
    "chaos",
    "death",
    "nature",
    "law",
    // Blood rune (RuneScape Classic, 2001-2002)
    "blood",
  ] as RuneVariant[],
} as const;

/**
 * Options for creating a single rune
 */
export interface RuneOptions {
  followAppTheme?: boolean;
  scale?: number;
}

/**
 * Arrangement types for composing multiple runes
 */
export type RuneArrangement = "grid" | "circle" | "scattered" | "stack";

/**
 * Options for arranging multiple runes
 */
export interface RuneArrangementOptions {
  runes: RuneVariant[];
  arrangement: RuneArrangement;
  spacing?: number;
  randomRotation?: boolean;
  scale?: number;
}

/**
 * Options for creating a rune pouch effect
 */
export interface RunePouchOptions {
  runes: RuneVariant[];
  spreadRadius?: number;
  scale?: number;
  randomRotation?: boolean;
}

/**
 * Color configuration for rune variants
 * Based on Runescape rune appearances
 */
const RUNE_COLORS: Record<RuneVariant, Color3> = {
  air: new Color3(0.9, 0.9, 0.95), // White/light gray
  astral: new Color3(0.8, 0.6, 0.9), // Purple/violet
  blood: new Color3(0.7, 0.1, 0.1), // Dark red
  body: new Color3(0.3, 0.5, 0.8), // Blue
  chaos: new Color3(0.9, 0.5, 0.1), // Orange
  cosmic: new Color3(0.9, 0.85, 0.2), // Yellow/gold
  death: new Color3(0.15, 0.15, 0.15), // Black
  dust: new Color3(0.6, 0.5, 0.4), // Tan/brown
  earth: new Color3(0.4, 0.25, 0.15), // Brown
  fire: new Color3(0.95, 0.3, 0.1), // Red-orange
  lava: new Color3(0.8, 0.2, 0.0), // Deep red-orange
  law: new Color3(0.2, 0.4, 0.8), // Royal blue
  mind: new Color3(0.9, 0.5, 0.3), // Orange-peach
  mist: new Color3(0.7, 0.8, 0.9), // Light blue-gray
  mud: new Color3(0.35, 0.25, 0.15), // Dark brown
  nature: new Color3(0.2, 0.6, 0.2), // Green
  smoke: new Color3(0.3, 0.3, 0.35), // Dark gray
  soul: new Color3(0.6, 0.4, 0.7), // Purple
  steam: new Color3(0.5, 0.6, 0.7), // Blue-gray
  water: new Color3(0.1, 0.4, 0.8), // Blue
  wrath: new Color3(0.5, 0.0, 0.0), // Deep crimson
};

/**
 * Manager responsible for rune creation and composition
 */
export class RuneManager extends BaseManager {
  private scene: Scene | null = null;
  private instanceCounter = 0;

  /**
   * Initialize the manager (required by BaseManager)
   */
  protected async onInitialize(): Promise<void> {
    // No async initialization needed
  }

  /**
   * Dispose the manager (required by BaseManager)
   */
  protected onDispose(): void {
    this.scene = null;
  }

  /**
   * Set the scene reference for material creation
   */
  setScene(scene: Scene): void {
    this.scene = scene;
  }

  /**
   * Get the current scene
   */
  getScene(): Scene | null {
    return this.scene;
  }

  /**
   * Get all supported rune variants
   */
  getSupportedVariants(): RuneVariant[] {
    return [...RUNE_VARIANTS];
  }

  /**
   * Create a single rune mesh
   *
   * Returns meshes with parent-child hierarchy:
   * - First mesh (base tablet) is the physics root with isPhysicsRoot=true
   * - Subsequent meshes (symbols) are parented to base and non-pickable
   */
  createRune(
    variant: RuneVariant,
    scene: Scene,
    options: RuneOptions = {},
  ): Mesh[] {
    const meshDataArray = getRuneMeshes(variant);
    const materials = getRuneMaterials(variant);
    const meshes: Mesh[] = [];

    // Generate unique instance ID for this rune
    const instanceId = this.instanceCounter++;

    for (const meshData of meshDataArray) {
      const mesh = this.createMeshFromData(
        meshData,
        variant,
        scene,
        options,
        materials,
        instanceId,
      );
      meshes.push(mesh);
    }

    // Establish parent-child hierarchy for physics
    // Base (primitive0) is the physics root, symbol (primitive1) is passenger
    if (meshes.length >= 2) {
      const baseMesh = meshes[0];
      // Mark base as physics root
      baseMesh.metadata = {
        ...baseMesh.metadata,
        isPhysicsRoot: true,
      };

      // Parent all other meshes (symbols) to the base
      for (let i = 1; i < meshes.length; i++) {
        const symbolMesh = meshes[i];
        // Parent to base - movement/rotation of base automatically moves symbol
        symbolMesh.parent = baseMesh;
        // Disable picking - raycasts pass through to base (the physics object)
        symbolMesh.isPickable = false;
        // Reset local scale to 1 - child inherits scale from parent
        // (prevents double-scaling: parent.scale × child.scale)
        symbolMesh.scaling.setAll(1);
      }
    } else if (meshes.length === 1) {
      // Single mesh is also the physics root
      meshes[0].metadata = {
        ...meshes[0].metadata,
        isPhysicsRoot: true,
      };
    }

    this.eventBus.emit("rune:created", { meshCount: meshes.length, variant });

    return meshes;
  }

  /**
   * Create multiple runes in an arrangement
   */
  createRuneArrangement(scene: Scene, options: RuneArrangementOptions): Mesh[] {
    const allMeshes: Mesh[] = [];
    const {
      arrangement,
      runes,
      spacing = 1.5,
      randomRotation = false,
      scale = 1,
    } = options;

    const positions = this.calculateArrangementPositions(
      runes.length,
      arrangement,
      spacing,
    );

    for (let i = 0; i < runes.length; i++) {
      const variant = runes[i];
      const meshes = this.createRune(variant, scene, { scale });

      const position = positions[i];
      for (const mesh of meshes) {
        mesh.position.addInPlace(position);

        if (randomRotation) {
          mesh.rotation.y = Math.random() * Math.PI * 2;
          mesh.rotation.x = (Math.random() - 0.5) * 0.3;
          mesh.rotation.z = (Math.random() - 0.5) * 0.3;
        }
      }

      allMeshes.push(...meshes);
    }

    this.eventBus.emit("rune:arrangement-created", {
      arrangement,
      count: runes.length,
    });

    return allMeshes;
  }

  /**
   * Create a scattered "pouch" arrangement of runes
   * Simulates runes spilled from a bag onto a surface
   */
  createRunePouch(scene: Scene, options: RunePouchOptions): Mesh[] {
    const {
      runes,
      spreadRadius = 2,
      scale = 0.8,
      randomRotation = true,
    } = options;

    const allMeshes: Mesh[] = [];

    for (const variant of runes) {
      const meshes = this.createRune(variant, scene, { scale });

      // Random position within spread radius
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * spreadRadius;
      const x = Math.cos(angle) * distance;
      const z = Math.sin(angle) * distance;

      // Slight Y variation for stacking effect
      const y = Math.random() * 0.1;

      for (const mesh of meshes) {
        mesh.position.addInPlace(new Vector3(x, y, z));

        if (randomRotation) {
          mesh.rotation.y = Math.random() * Math.PI * 2;
          // Flat-ish rotation (like runes lying on table)
          mesh.rotation.x = (Math.random() - 0.5) * 0.5;
          mesh.rotation.z = (Math.random() - 0.5) * 0.5;
        }
      }

      allMeshes.push(...meshes);
    }

    this.eventBus.emit("rune:pouch-created", { count: runes.length });

    return allMeshes;
  }

  /**
   * Calculate positions for arrangement patterns
   */
  private calculateArrangementPositions(
    count: number,
    arrangement: RuneArrangement,
    spacing: number,
  ): Vector3[] {
    const positions: Vector3[] = [];

    switch (arrangement) {
      case "grid": {
        const cols = Math.ceil(Math.sqrt(count));
        const rows = Math.ceil(count / cols);
        const zOffset = ((rows - 1) * spacing) / 2;
        for (let i = 0; i < count; i++) {
          const row = Math.floor(i / cols);
          const col = i % cols;
          // Calculate row offset to center items in each row
          const itemsInRow = Math.min(count - row * cols, cols);
          const rowOffset = ((itemsInRow - 1) * spacing) / 2;
          positions.push(
            new Vector3(col * spacing - rowOffset, 0, row * spacing - zOffset),
          );
        }
        break;
      }

      case "circle": {
        const radius = (count * spacing) / (2 * Math.PI);
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          positions.push(
            new Vector3(Math.cos(angle) * radius, 0, Math.sin(angle) * radius),
          );
        }
        break;
      }

      case "scattered": {
        for (let i = 0; i < count; i++) {
          const angle = Math.random() * Math.PI * 2;
          const distance = (Math.random() * spacing * Math.sqrt(count)) / 2;
          positions.push(
            new Vector3(
              Math.cos(angle) * distance,
              Math.random() * 0.1,
              Math.sin(angle) * distance,
            ),
          );
        }
        break;
      }

      case "stack": {
        for (let i = 0; i < count; i++) {
          const offsetX = (Math.random() - 0.5) * 0.2;
          const offsetZ = (Math.random() - 0.5) * 0.2;
          positions.push(new Vector3(offsetX, i * 0.15, offsetZ));
        }
        break;
      }
    }

    return positions;
  }

  /**
   * Create a single mesh from mesh data
   */
  private createMeshFromData(
    meshData: RuneMesh,
    variant: RuneVariant,
    scene: Scene,
    options: RuneOptions,
    materials: RuneMaterials,
    instanceId: number,
  ): Mesh {
    // Use unique name to ensure each rune has its own physics body
    const uniqueName = `rune_${variant}_${meshData.name}_${instanceId}`;
    const mesh = new Mesh(uniqueName, scene);

    // Apply vertex data
    const vertexData = new VertexData();
    vertexData.positions = Array.from(meshData.positions);
    vertexData.indices = Array.from(meshData.indices);
    vertexData.normals = Array.from(meshData.normals);
    if (meshData.uvs) {
      vertexData.uvs = Array.from(meshData.uvs);
    }
    vertexData.applyToMesh(mesh);

    // Apply scale
    const scale = options.scale ?? 1;
    mesh.scaling.setAll(scale);

    // Get extracted material if available
    const extractedMaterial =
      meshData.materialIndex !== undefined
        ? materials[meshData.materialIndex]
        : undefined;

    // Create material with rune-specific color (uses extracted or falls back to hardcoded)
    const material = this.createRuneMaterial(
      scene,
      variant,
      uniqueName,
      extractedMaterial,
    );
    mesh.material = material;

    // Store metadata
    mesh.metadata = {
      runeOptions: options,
      type: "rune",
      variant,
    };

    return mesh;
  }

  /**
   * Create PBR material for a specific rune variant
   * Uses extracted material colors when available, falls back to RUNE_COLORS
   */
  private createRuneMaterial(
    scene: Scene,
    variant: RuneVariant,
    meshName: string,
    extractedMaterial?: {
      albedoColor?: [number, number, number];
      metallic?: number;
      roughness?: number;
      emissiveColor?: [number, number, number];
      alpha?: number;
    },
  ): PBRMaterial {
    const material = new PBRMaterial(
      `runeMaterial_${variant}_${meshName}`,
      scene,
    );

    // Use extracted material colors if available, otherwise fall back to hardcoded RUNE_COLORS
    let baseColor: Color3;
    if (extractedMaterial?.albedoColor) {
      baseColor = new Color3(
        extractedMaterial.albedoColor[0],
        extractedMaterial.albedoColor[1],
        extractedMaterial.albedoColor[2],
      );
      material.metallic = extractedMaterial.metallic ?? 0.1;
      material.roughness = extractedMaterial.roughness ?? 0.6;

      if (extractedMaterial.emissiveColor) {
        material.emissiveColor = new Color3(
          extractedMaterial.emissiveColor[0],
          extractedMaterial.emissiveColor[1],
          extractedMaterial.emissiveColor[2],
        );
      } else {
        material.emissiveColor = baseColor.scale(0.1);
      }
    } else {
      // Fallback to hardcoded rune colors
      baseColor = RUNE_COLORS[variant];
      material.metallic = 0.1;
      material.roughness = 0.6;
      material.emissiveColor = baseColor.scale(0.1); // Subtle magical glow
    }

    // Rune tablet material - slightly rough stone-like appearance
    material.albedoColor = baseColor;
    material.environmentIntensity = 0.4;

    material.backFaceCulling = true;
    material.twoSidedLighting = true;

    return material;
  }

  /**
   * Apply theme colors to rune meshes
   */
  applyThemeColors(meshes: Mesh[], _theme: string, isDarkMode: boolean): void {
    for (const mesh of meshes) {
      const material = mesh.material as PBRMaterial;
      const variant = mesh.metadata?.variant as RuneVariant | undefined;

      if (material && variant) {
        // Check if this is the grey rock material (don't apply variant hue to rock)
        const isGreyRock =
          Math.abs(material.albedoColor.r - 0.2157) < 0.05 &&
          Math.abs(material.albedoColor.g - 0.2157) < 0.05 &&
          Math.abs(material.albedoColor.b - 0.2157) < 0.05;

        // Check if this is a dark material (e.g., black eyes/mouth on Chaos rune)
        const isDarkMaterial =
          material.albedoColor.r < 0.1 &&
          material.albedoColor.g < 0.1 &&
          material.albedoColor.b < 0.1;

        if (isGreyRock || isDarkMaterial) {
          // Rock and dark materials (eyes/mouth) keep their own color
          material.emissiveColor = material.albedoColor.scale(
            isDarkMode ? 0.1 : 0.03,
          );
          material.environmentIntensity = isDarkMode ? 0.5 : 0.4;
        } else {
          // Symbol gets variant-colored emissive glow
          const baseColor = RUNE_COLORS[variant];
          material.emissiveColor = baseColor.scale(isDarkMode ? 0.15 : 0.05);
          material.environmentIntensity = isDarkMode ? 0.5 : 0.4;
        }
      }
    }

    this.eventBus.emit("rune:theme-applied", { isDarkMode });
  }
}
